import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy"
        description="Learn how Instant-Email protects your privacy. We don't store personal data, and all temporary emails auto-delete after 24 hours."
        path="/privacy"
      />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">Last updated: January 3, 2026</p>

            <Card className="bg-card border-border">
              <CardContent className="p-8 prose prose-slate dark:prose-invert max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
                  <p>
                    Welcome to Instant-Email. We are committed to protecting your privacy and ensuring the security 
                    of your information. This Privacy Policy explains how we collect, use, and protect your data when 
                    you use our temporary email service at instant-email.org.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                  <h3 className="text-xl font-semibold mb-3">2.1 Temporary Email Addresses</h3>
                  <p>
                    When you generate a temporary email address on our platform, we create a disposable email address 
                    that automatically expires after 24 hours. We do not require you to provide any personal information 
                    to use this service.
                  </p>
                  
                  <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Email Messages</h3>
                  <p>
                    Incoming emails sent to your temporary address are stored temporarily on our servers. All emails 
                    and associated data are automatically deleted after 24 hours or when the temporary email address expires.
                  </p>
                  
                  <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Registered Accounts (Optional)</h3>
                  <p>
                    If you choose to create an account, we collect:
                  </p>
                  <ul>
                    <li>Email address (for account authentication)</li>
                    <li>Username</li>
                    <li>Password (encrypted)</li>
                    <li>Account creation date</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold mb-3 mt-6">2.4 Technical Information</h3>
                  <p>
                    We automatically collect certain technical information including:
                  </p>
                  <ul>
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Device information</li>
                    <li>Access times and dates</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
                  <p>We use the collected information to:</p>
                  <ul>
                    <li>Provide and maintain our temporary email service</li>
                    <li>Generate and manage temporary email addresses</li>
                    <li>Receive and display incoming emails</li>
                    <li>Improve and optimize our service</li>
                    <li>Prevent fraud and abuse</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
                  <p>
                    We implement industry-standard security measures to protect your data. All temporary emails and 
                    addresses are automatically deleted after 24 hours. User passwords are encrypted using secure 
                    hashing algorithms. We do not sell, trade, or rent your personal information to third parties.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">5. Cookies and Tracking</h2>
                  <p>
                    We use cookies and similar technologies to enhance your experience and analyze service usage. 
                    We use PostHog for analytics to understand how users interact with our service. You can control 
                    cookie preferences through your browser settings.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">6. Third-Party Services</h2>
                  <p>
                    Our service may use third-party services for analytics, hosting, and performance monitoring. 
                    These third parties have access to your information only to perform specific tasks on our behalf 
                    and are obligated not to disclose or use it for any other purpose.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
                  <p>
                    Temporary email addresses and all associated messages are automatically deleted after 24 hours. 
                    For registered accounts, we retain your account information until you choose to delete your account.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">8. Your Rights</h2>
                  <p>You have the right to:</p>
                  <ul>
                    <li>Access your personal information</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data</li>
                    <li>Object to data processing</li>
                    <li>Data portability</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
                  <p>
                    Our service is not intended for children under 13 years of age. We do not knowingly collect 
                    personal information from children under 13.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">10. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. We will notify you of any changes by 
                    posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy, please contact us at:
                  </p>
                  <p className="mt-4">
                    <strong>Email:</strong> privacy@instant-email.org<br />
                    <strong>Website:</strong> <a href="https://instant-email.org" className="text-primary hover:underline">instant-email.org</a>
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
